export interface CountryRedirectContext {
  url: string;
  lang?: string;
  language?: string;
}

const TOKEN_PATTERN = /(\{url\}|\{lang\}|\{language\})/g;

export function replaceDestinationUrl(template: string, url: string): string {
  return (template ?? '').replaceAll('{url}', url);
}

export function getDestinationHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return url;
  }
}

export function hasCountryRedirectCopy(
  headingTemplate: string,
  bodyTemplate: string,
  languageTemplate?: string
): boolean {
  return Boolean(headingTemplate?.trim() || bodyTemplate?.trim() || languageTemplate?.trim());
}

export function populateCountryRedirectElement(
  element: HTMLElement,
  template: string,
  context: CountryRedirectContext,
  options?: { urlAsHostname?: boolean }
): void {
  element.replaceChildren();

  (template ?? '').split(TOKEN_PATTERN).forEach((segment) => {
    if (!segment) {
      return;
    }

    switch (segment) {
      case '{url}': {
        if (options?.urlAsHostname) {
          const strong = document.createElement('strong');

          strong.textContent = getDestinationHost(context.url);
          element.append(strong);
        } else {
          element.append(document.createTextNode(replaceDestinationUrl('{url}', context.url)));
        }

        return;
      }
      case '{lang}':
        if (context.lang) {
          element.append(document.createTextNode(context.lang));
        }

        return;
      case '{language}':
        if (context.language) {
          element.append(document.createTextNode(context.language));
        }

        return;
      default:
        element.append(document.createTextNode(segment));
    }
  });
}

export function setCountryRedirectHeading(element: HTMLElement, template: string, context: CountryRedirectContext): void {
  populateCountryRedirectElement(element, template, context);
}

export function setCountryRedirectBody(element: HTMLElement, template: string, context: CountryRedirectContext): void {
  populateCountryRedirectElement(element, template, context, { urlAsHostname: true });
}

export function setCountryRedirectLanguage(element: HTMLElement, template: string, context: CountryRedirectContext): void {
  populateCountryRedirectElement(element, template, context);
}

export function resolveCountryRedirectLanguageTemplate(languageTemplate: string, bodyTemplate: string): string | undefined {
  if (languageTemplate?.trim()) {
    return languageTemplate;
  }

  return splitCountryRedirectBodyTemplate(bodyTemplate).language;
}

export function resolveCountryRedirectBodyTemplate(languageTemplate: string, bodyTemplate: string): string {
  if (languageTemplate?.trim()) {
    return bodyTemplate;
  }

  return splitCountryRedirectBodyTemplate(bodyTemplate).message;
}

export function splitCountryRedirectBodyTemplate(template: string): { message: string; language?: string } {
  const [message = '', languageLine] = (template ?? '').split(/\r?\n/, 2);
  const language = languageLine?.trim();

  return {
    message,
    ...(language ? { language } : null),
  };
}
